import { BarChart, Bar, XAxis, LabelList, ResponsiveContainer, YAxis } from 'recharts'
import { useTheme } from 'styled-components'

import { ChartData, Value } from '../../types'

const MAX_LABEL_LETTERS = 25
const MAX_LABEL_WIDTH = 200
const HUNDRED_PERCENT = 100

interface Props {
  data: ChartData[]
  isAreaActiveView: boolean
}

const getMaxLabelWidth = (data: { name: string }[]) => {
  const maxLength = Math.max(...data.map(({ name }) => name.length))
  return maxLength * 8 > MAX_LABEL_WIDTH ? MAX_LABEL_WIDTH : maxLength * 8
}

const CustomYAxisTick = ({
  x,
  y,
  payload,
}: {
  x: number
  y: number
  payload: { value: string }
}) => {
  const value = payload.value
  const label =
    value.length > MAX_LABEL_LETTERS ? `${value.substring(0, MAX_LABEL_LETTERS)}...` : value

  return (
    <text x={x} y={y + 5} textAnchor="end" fill={'black'}>
      {label}
    </text>
  )
}

export const Chart: React.FC<Props> = ({ data, isAreaActiveView }) => {
  const { colors } = useTheme()
  const height = data.length * 50
  const yAxisWidth = getMaxLabelWidth(data)

  const valueFormatter = (value: Value) => {
    if (value.harvested <= 0) return null
    const harvestedQty = isAreaActiveView ? `${value.harvested} ha.` : value.harvested
    const label = `${value.percentage}% (${harvestedQty})`

    return label
  }

  const totalFormatter = (value: ChartData['values']) => {
    const showLabel = value.current.harvested <= 0 && value.nextTwoWeeks.harvested <= 0
    const harvestedQty = isAreaActiveView ? '0 ha.' : '0'
    const label = showLabel ? `0% (${harvestedQty})` : null
    return label
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart layout="vertical" data={data} margin={{ right: 70, left: 10, bottom: 5 }}>
        <XAxis type="number" tick={false} hide />
        <YAxis type="category" dataKey="name" width={yAxisWidth} tick={CustomYAxisTick} />
        <Bar
          stackId={'stackA'}
          dataKey={'values.current.barValue'}
          fill={colors.milestoneUpdateType.MANUAL}
          isAnimationActive={false}
          stroke={colors.primary[600]}
          strokeWidth={1}
          barSize={25}
        >
          <LabelList
            dataKey="values.current"
            position="center"
            fill={colors.black}
            formatter={valueFormatter}
            fontSize={12}
          />
        </Bar>
        <Bar
          stackId={'stackA'}
          dataKey={'values.nextTwoWeeks.barValue'}
          fill={colors.milestoneUpdateType.AUTOMATIC_CONVERSION}
          isAnimationActive={false}
          stroke={colors.primary[600]}
          strokeWidth={1}
          barSize={25}
        >
          <LabelList
            dataKey="values.nextTwoWeeks"
            position="center"
            fill={colors.black}
            formatter={valueFormatter}
            fontSize={12}
          />
        </Bar>
        <Bar
          stackId={'stackA'}
          dataKey={props =>
            HUNDRED_PERCENT - props.values.current.barValue - props.values.nextTwoWeeks.barValue
          }
          barSize={25}
          fill={colors.white}
          isAnimationActive={false}
          stroke={colors.primary[600]}
          strokeWidth={1}
        >
          <LabelList
            dataKey={props => {
              const dataProps = props as ChartData
              return isAreaActiveView
                ? `${dataProps.values.current.total} ha.`
                : dataProps.values.current.total
            }}
            position="right"
            fill={colors.black}
          />
          <LabelList
            dataKey="values"
            position="center"
            fill={colors.black}
            formatter={totalFormatter}
            fontSize={12}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}
